import React from "react";

import { Avatar, Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";

import { getContrastColor } from "../../utils/contrast-color";

export interface TeamChipProps {
  teamId: string;
  name: string;
  color?: string;
  showAvatar?: boolean;
  bold?: boolean;
  sx?: SxProps<Theme>;
}

const TeamChip: React.FC<TeamChipProps> = ({
  teamId,
  name,
  color = "#64748b",
  showAvatar = false,
  bold = true,
  sx,
}) => {
  const theme = useTheme();
  const contrastColor = getContrastColor(color);
  const initials =
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0] || "")
      .join("")
      .toUpperCase() || "?";

  return (
    <Box
      key={teamId}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        py: showAvatar ? 0 : 0.5,
        pl: showAvatar ? 0 : 1,
        pr: 1,
        borderRadius: 1,
        backgroundColor: alpha(color, 0.13),
        border: "1px solid",
        borderColor: color,
        boxSizing: "border-box",
        maxWidth: "100%",
        ...sx,
      }}
    >
      {showAvatar && (
        <Avatar
          variant="square"
          sx={{
            bgcolor: color,
            color: contrastColor,
            width: 24,
            height: 24,
            fontSize: "0.75rem",
            fontWeight: 700,
            flexShrink: 0,
            borderTopLeftRadius: 1,
            borderBottomLeftRadius: 1,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          }}
        >
          {initials}
        </Avatar>
      )}
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.primary,
          maxWidth: 300,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          lineHeight: 1.2,
          fontWeight: bold ? 600 : 400,
        }}
      >
        {name}
      </Typography>
    </Box>
  );
};

export default TeamChip;
